/**
 * Debrief (§6, §7.1 view 8): score card, grades, the annotated timeline, and three concrete
 * lessons generated from rules rather than written per scenario.
 */

import { useSession } from '../../store/session';
import { useEngine } from '../useEngine';
import { getSite } from '../../sites';
import { getScenario } from '../../scenarios';
import { Trend } from '../charts/Trend';
import { moneyExact, money } from '../format';

const GRADE_COLOUR: Record<string, string> = {
  A: 'var(--ok)',
  B: 'var(--ok)',
  C: 'var(--warn)',
  D: 'var(--warn)',
  F: 'var(--alarm)',
};

export function Debrief(): JSX.Element {
  const card = useSession((s) => s.card);
  const snap = useSession((s) => s.snapshot);
  const setPhase = useSession((s) => s.setPhase);
  const site = getSite(useSession((s) => s.siteId));
  const scenario = getScenario(useSession((s) => s.scenarioId));
  const engine = useEngine();

  if (!card || !snap) {
    return <div style={{ padding: 'var(--s5)', color: 'var(--ink-dim)' }}>The shift is still running.</div>;
  }

  return (
    <div className="scroll" style={{ padding: 'var(--s4)' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gap: 'var(--s3)' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div className="label">{site.name} · debrief</div>
            <h1 className="title" style={{ fontSize: 28, margin: 0 }}>
              {scenario.name}
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
            <div>
              <div className="label">Result</div>
              <div className="title" style={{ fontSize: 22, color: card.passed ? 'var(--ok)' : 'var(--warn)' }}>
                {card.passed ? 'Passed' : 'Not passed'}
              </div>
            </div>
            <div>
              <div className="label">Grade</div>
              <div className="title" style={{ fontSize: 34, color: GRADE_COLOUR[card.grade] }}>
                {card.grade}
              </div>
            </div>
            <div>
              <div className="label">Net</div>
              <div className="readout" style={{ color: card.net < 0 ? 'var(--alarm)' : 'var(--ink)' }}>
                {money(card.net)}
              </div>
            </div>
          </div>
        </header>

        <section className="panel">
          <div className="label" style={{ marginBottom: 8 }}>
            Three lessons from this shift
          </div>
          <ol style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 8 }}>
            {card.lessons.map((l, i) => (
              <li key={i} style={{ lineHeight: 1.5 }}>
                {l}
              </li>
            ))}
          </ol>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 'var(--s3)' }}>
          <section className="panel">
            <div className="label" style={{ marginBottom: 8 }}>
              Score card
            </div>
            <table style={{ width: '100%', fontSize: 13 }} className="num">
              <tbody>
                <Row label="Energy" v={card.energyRevenue} />
                <Row label="Spinning reserve" v={card.reserveRevenue} />
                <Row label="AGC" v={card.agcRevenue} />
                <Row label="Emergency bonuses" v={card.bonusRevenue} />
                <Row label="Penalties" v={-card.penalties} />
                <Row label="Start costs" v={-card.startCosts} />
                <tr style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ paddingTop: 6, fontWeight: 600 }}>Net</td>
                  <td
                    style={{
                      paddingTop: 6,
                      textAlign: 'right',
                      fontWeight: 600,
                      color: card.net < 0 ? 'var(--alarm)' : 'var(--ok)',
                    }}
                  >
                    {moneyExact(card.net)}
                  </td>
                </tr>
              </tbody>
            </table>

            {Object.keys(card.penaltyByKind).length > 0 && (
              <>
                <div className="label" style={{ margin: '12px 0 4px' }}>
                  Penalties by cause
                </div>
                <table style={{ width: '100%', fontSize: 12 }} className="num">
                  <tbody>
                    {Object.entries(card.penaltyByKind)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ color: 'var(--ink-dim)' }}>{k.replace(/-/g, ' ')}</td>
                          <td style={{ textAlign: 'right', color: 'var(--alarm)' }}>{moneyExact(-v)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          <section className="panel">
            <div className="label" style={{ marginBottom: 8 }}>
              Grades and accounting
            </div>
            <table style={{ width: '100%', fontSize: 13 }} className="num">
              <tbody>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Safety grade</td>
                  <td style={{ textAlign: 'right', color: GRADE_COLOUR[card.safetyGrade], fontWeight: 600 }}>
                    {card.safetyGrade}
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Dam-safety incidents</td>
                  <td style={{ textAlign: 'right' }}>{card.safetyIncidents}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Environmental compliance</td>
                  <td style={{ textAlign: 'right' }}>{card.envCompliancePct.toFixed(1)} %</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Environmental strikes</td>
                  <td style={{ textAlign: 'right' }}>{card.envStrikes}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Flood damages</td>
                  <td style={{ textAlign: 'right' }}>{moneyExact(card.floodDamage)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Do-nothing baseline</td>
                  <td style={{ textAlign: 'right' }}>{moneyExact(card.baselineFloodDamage)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Damages avoided</td>
                  <td style={{ textAlign: 'right', color: 'var(--ok)' }}>{moneyExact(card.damagesAvoided)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Energy</td>
                  <td style={{ textAlign: 'right' }}>{Math.round(card.energyMWh).toLocaleString('en-US')} MWh</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Water spilled</td>
                  <td style={{ textAlign: 'right' }}>{(card.spilledVolume / 1e6).toFixed(1)} ×10⁶ m³</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Revenue foregone on spill</td>
                  <td style={{ textAlign: 'right' }}>{moneyExact(card.spillOpportunityCost)}</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Efficiency</td>
                  <td style={{ textAlign: 'right' }}>
                    {card.efficiency.toFixed(1)} / {card.efficiencyBenchmark.toFixed(1)} MWh per 10⁶ m³
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--ink-dim)' }}>Rule-curve excursion</td>
                  <td style={{ textAlign: 'right' }}>{card.bandMetreDays.toFixed(1)} metre-days</td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>

        <section className="panel">
          <div className="label" style={{ marginBottom: 8 }}>
            Objectives
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {card.objectives.length === 0 && <div style={{ color: 'var(--ink-dim)' }}>Sandbox — no objectives.</div>}
            {card.objectives.map((o) => (
              <div
                key={o.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '5px 8px',
                  background: 'var(--surface-2)',
                  borderLeft: `3px solid ${o.met ? 'var(--ok)' : 'var(--alarm)'}`,
                }}
              >
                <span style={{ color: o.met ? 'var(--ok)' : 'var(--alarm)', fontWeight: 700 }}>
                  {o.met ? '✓' : '✕'}
                </span>
                <span style={{ flex: 1 }}>{o.text}</span>
                <span className="num" style={{ color: 'var(--ink-dim)' }}>
                  {o.detail}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="label" style={{ marginBottom: 8 }}>
            Timeline — elevation and release across the shift
          </div>
          <Trend
            height={200}
            yLabel="m ASL"
            fmt={(v) => v.toFixed(1)}
            series={[{ x: snap.history.coarseT, y: snap.history.coarseHeadwater, colour: 'var(--water-shallow)', width: 2, label: 'headwater' }]}
            thresholds={[
              { value: site.dam.fsl, colour: 'var(--ink-dim)', label: 'FSL' },
              { value: site.dam.mfl, colour: 'var(--warn)', label: 'MFL' },
              { value: site.dam.mol, colour: 'var(--ink-dim)', label: 'MOL' },
            ]}
          />
          <Trend
            height={190}
            yLabel="m³/s"
            fmt={(v) => Math.round(v).toLocaleString('en-US')}
            series={[
              { x: snap.history.coarseT, y: snap.history.coarseInflow, colour: 'var(--ok)', width: 1.6, label: 'inflow' },
              { x: snap.history.coarseT, y: snap.history.coarseOutflow, colour: 'var(--water-shallow)', width: 1.6, label: 'release' },
              { x: snap.history.coarseT, y: snap.history.coarseGauge, colour: 'var(--ink-dim)', width: 1.3, label: site.downstream.gaugeName },
            ]}
            thresholds={[{ value: site.downstream.floodMinor, colour: 'var(--warn)', label: 'minor flood' }]}
          />
        </section>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingBottom: 'var(--s6)' }}>
          <button onClick={() => setPhase('launcher')}>Back to campaign</button>
          <button
            className="primary"
            onClick={() =>
              engine.start(
                scenario.siteId,
                scenario.id,
                useSession.getState().difficulty,
                useSession.getState().seed,
              )
            }
          >
            Run this shift again
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, v }: { label: string; v: number }): JSX.Element {
  return (
    <tr>
      <td style={{ color: 'var(--ink-dim)' }}>{label}</td>
      <td style={{ textAlign: 'right', color: v < 0 ? 'var(--alarm)' : 'var(--ink)' }}>{moneyExact(v)}</td>
    </tr>
  );
}
